import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Global error handler component that catches unhandled JavaScript errors
 * and unhandled promise rejections
 */
export default function GlobalErrorHandler() {
  const navigate = useNavigate();
  
  useEffect(() => {
    // Handle uncaught JavaScript errors
    const handleError = (event: ErrorEvent) => {
      console.error('Uncaught error:', {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        error: event.error
      });

      // Check if it's a network-related error
      const isNetworkError = 
        event.message.includes('fetch') ||
        event.message.includes('NetworkError') ||
        event.message.includes('Failed to fetch') ||
        event.message.includes('net::');

      if (isNetworkError && !navigator.onLine) {
        // Navigate to offline page for network errors when offline
        navigate('/offline');
        return;
      }

      // For other errors, show a user-friendly message
      if (import.meta.env.PROD) {
        // In production, we might want to show a toast or modal
        // For now, just log it
        console.error('An unexpected error occurred. Please refresh the page.');
      }
    };

    // Handle unhandled promise rejections
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      console.error('Unhandled promise rejection:', event.reason);

      // Check if it's a network-related rejection
      const reason = event.reason;
      const isNetworkError = 
        (reason instanceof Error && (
          reason.message.includes('fetch') ||
          reason.message.includes('NetworkError') ||
          reason.message.includes('Failed to fetch') ||
          reason.message.includes('net::') ||
          reason.name === 'TimeoutError' ||
          reason.name === 'NetworkError'
        )) ||
        (typeof reason === 'string' && (
          reason.includes('fetch') ||
          reason.includes('network') ||
          reason.includes('timeout')
        ));

      if (isNetworkError && !navigator.onLine) {
        // Navigate to offline page for network-related rejections when offline
        navigate('/offline');
        return;
      }

      // Prevent the browser from showing the default unhandled rejection error
      if (import.meta.env.PROD) {
        event.preventDefault();
      }
    };

    // Add event listeners
    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    // Cleanup on unmount
    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, [navigate]);

  // This component doesn't render anything
  return null;
}